import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import DistributorConfiguration from "./DistributorConfiguration";

const DISTRIBUTOR_STATUS = [
  { name: "Mouser", value: "Mouser" },
  { name: "Digi-Key", value: "Digikey" },
  { name: "TTI", value: "TTI" },
  { name: "Arrow", value: "Arrow" },
  { name: "TI", value: "TI" },
  { name: "Element 14", value: "Element14" },
  { name: "TME", value: "TME" },
  { name: "Rutronik", value: "Rutronik" },
  { name: "Avnet", value: "Avnet" },
];

// Badge + display name per distributor (colours match the prototype).
const DIST_META = {
  Mouser: { short: "Mou", name: "Mouser", bg: "#003478", fg: "#FFFFFF" },
  Digikey: { short: "DK", name: "Digi-Key", bg: "#CC0000", fg: "#FFFFFF" },
  Element14: { short: "E14", name: "Element 14", bg: "#FF6900", fg: "#FFFFFF" },
  Arrow: { short: "Arr", name: "Arrow", bg: "#FFB300", fg: "#1A1A1A" },
  TTI: { short: "TTI", name: "TTI", bg: "#0F6CBD", fg: "#FFFFFF" },
  TME: { short: "TME", name: "TME", bg: "#1F8A5B", fg: "#FFFFFF" },
  TI: { short: "TI", name: "TI", bg: "#C8102E", fg: "#FFFFFF" },
  Rutronik: { short: "Rut", name: "Rutronik", bg: "#00509E", fg: "#FFFFFF" },
  Avnet: { short: "Avn", name: "Avnet", bg: "#5F259F", fg: "#FFFFFF" },
};

const DistributorConfigModal = ({
  isDistributorConfigModalOpen,
  setIsDistributorConfigModalOpen,
  selectedDistributor = null,
  initialValues = null,
  defaultDistributor = "Mouser",
  onSaveSuccess = null,
}) => {
  const [selectedDistributorType, setSelectedDistributorType] = useState(
    selectedDistributor || defaultDistributor,
  );
  const [isSaving, setIsSaving] = useState(false);

  // On each open, default to the edited distributor (edit) or Mouser (configure).
  useEffect(() => {
    if (isDistributorConfigModalOpen) {
      setSelectedDistributorType(selectedDistributor || defaultDistributor);
    }
  }, [isDistributorConfigModalOpen, selectedDistributor, defaultDistributor]);

  const handleClose = () => {
    setSelectedDistributorType(selectedDistributor || defaultDistributor);
    setIsDistributorConfigModalOpen(false);
  };

  const handleSave = () => {
    setIsSaving(true);
    // Trigger save in the DistributorConfiguration component
    const saveButton = document.querySelector("[data-save-distributor-config]");
    if (saveButton) {
      saveButton.click();
      // Don't close modal immediately - let the save success callback handle it
    }
  };

  const handleSaveSuccess = () => {
    setIsSaving(false);
    // Call the parent onSaveSuccess callback if provided
    if (onSaveSuccess && typeof onSaveSuccess === "function") {
      onSaveSuccess();
    }
    // Close modal after successful save
    handleClose();
  };

  const meta = DIST_META[selectedDistributorType];

  return (
    <Dialog
      open={isDistributorConfigModalOpen}
      onOpenChange={setIsDistributorConfigModalOpen}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[560px] sm:w-full p-0 gap-0 overflow-hidden rounded-xl border border-gray-200/80 shadow-xl bg-white">
        <DialogHeader className="px-6 pt-6 pb-5 border-b border-s360-stroke1">
          <div className="flex items-start gap-3">
            {meta ? (
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[12px] font-bold"
                style={{ backgroundColor: meta.bg, color: meta.fg }}
              >
                {meta.short}
              </span>
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-s360-violet-50 text-s360-violet text-[12px] font-bold">
                API
              </span>
            )}
            <div className="min-w-0 pt-0.5">
              <DialogTitle className="text-xl font-semibold tracking-tight text-s360-fg1">
                {meta ? `Configure ${meta.name}` : "Configure distributor"}
              </DialogTitle>
              <p className="mt-1 text-sm text-s360-fg3 leading-relaxed">
                API credentials &amp; ordering settings for this distributor
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {!selectedDistributor && (
            <div className="space-y-1.5">
              <Label
                htmlFor="distributor-select"
                className="text-xs font-semibold text-s360-fg2"
              >
                Distributor
              </Label>
              <Select
                value={selectedDistributorType}
                onValueChange={setSelectedDistributorType}
              >
                <SelectTrigger className="h-10 bg-white border-s360-stroke1 focus-visible:ring-2 focus-visible:ring-s360-violet/20 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select a distributor to configure" />
                </SelectTrigger>
                <SelectContent>
                  {DISTRIBUTOR_STATUS.map((distributor) => (
                    <SelectItem key={distributor.value} value={distributor.value}>
                      {distributor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedDistributorType && (
            <DistributorConfiguration
              selectedDistributor={selectedDistributorType}
              isModal={true}
              initialValues={initialValues}
              onSaveSuccess={handleSaveSuccess}
            />
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-s360-stroke1 bg-s360-stripe flex-row justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            className="min-w-[88px] rounded-md border border-s360-stroke1 bg-white text-s360-fg1 shadow-sm hover:bg-s360-bg2 transition-colors"
            onClick={handleClose}
          >
            Cancel
          </Button>
          {selectedDistributorType && (
            <Button
              type="button"
              variant="primary"
              className="min-w-[88px] rounded-md bg-s360-violet text-white shadow-sm hover:bg-s360-violet-600 active:bg-s360-violet-700 transition-colors disabled:opacity-60"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                "Save configuration"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DistributorConfigModal;
